import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router';
import { ThemeModeProvider } from './ThemeModeContext';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CareerDetailsHeader from './components/CareerDetailsHeader';
import ApplyJobHeader from './components/ApplyJobHeader';
import StartInterviewHeader from './components/StartInterviewHeader';
import VisitTracker from './visits/VisitTracker';
import Footer from './components/Footer';
import Home from './components/Home';
import { MarketProvider } from './lib/market';
import MarketSync from './components/MarketSync';
import ProtectedRoute, { RequireCapability } from './components/ProtectedRoute';
import LandingPage from './components/LandingPage';
import { LANDING_PATHS } from './landingPages';
import { brand } from './brand';
import { AuthProvider, useAuth } from './AuthContext';

// Every screen but the home page and the pages written for search engines is loaded when it is first wanted, so a first visit to the
// home page does not download the code for all of them. The signed-in frame (and the voice and chat libraries in it) is one of them.
const InternalLayout = lazy(() => import('./components/InternalLayout'));
const ContactSales = lazy(() => import('./components/ContactSales'));
const Visitors = lazy(() => import('./components/Visitors'));
const JobList = lazy(() => import('./components/JobList'));
const CreateJob = lazy(() => import('./components/CreateJob'));
const JobDetails = lazy(() => import('./components/JobDetails'));
const Careers = lazy(() => import('./components/Careers'));
const CareerDetails = lazy(() => import('./components/CareerDetails'));
const ApplyJob = lazy(() => import('./components/ApplyJob'));
const StartInterview = lazy(() => import('./components/StartInterview'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Interviews = lazy(() => import('./components/Interviews'));
const Candidates = lazy(() => import('./components/Candidates'));
const Resumes = lazy(() => import('./components/Resumes'));
const Match = lazy(() => import('./components/Match'));
const Login = lazy(() => import('./components/Login'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const SetPassword = lazy(() => import('./components/SetPassword'));
const Signup = lazy(() => import('./components/Signup'));
const SignupComplete = lazy(() => import('./components/SignupComplete'));
const EmployerSignup = lazy(() => import('./components/EmployerSignup'));
const Administration = lazy(() => import('./components/Administration'));
const PeoplePage = lazy(() => import('./components/Administration').then((module) => ({ default: module.PeoplePage })));
const Cost = lazy(() => import('./components/Cost'));
const Profile = lazy(() => import('./components/Profile'));
const CandidateWorkspace = lazy(() => import('./components/CandidateWorkspace'));
const MockInterview = lazy(() => import('./components/MockInterview'));
const MockAssessment = lazy(() => import('./components/MockAssessment'));
const TasksWorkspace = lazy(() => import('./components/TasksWorkspace'));
const NotesWorkspace = lazy(() => import('./components/NotesWorkspace'));
const PersonalCategories = lazy(() => import('./components/PersonalCategories').then((module) => ({ default: module.PersonalCategories })));
const PersonalCategoryItems = lazy(() => import('./components/PersonalCategories').then((module) => ({ default: module.PersonalCategoryItems })));
const SearchResults = lazy(() => import('./components/SearchResults'));
const SharedWithMe = lazy(() => import('./components/SharedWithMe'));
const Calendar = lazy(() => import('./components/Calendar'));
const Network = lazy(() => import('./components/Network'));
const Events = lazy(() => import('./components/Events'));
const Feedback = lazy(() => import('./components/Feedback'));
const JobBoard = lazy(() => import('./components/JobBoard'));
const Learning = lazy(() => import('./components/Learning'));
const PrivacyPolicy = lazy(() => import('./components/LegalPages').then((module) => ({ default: module.PrivacyPolicy })));
const TermsAndConditions = lazy(() => import('./components/LegalPages').then((module) => ({ default: module.TermsAndConditions })));
const JobBot = lazy(() => import('./components/JobBot'));
const ConfirmApplicationEmail = lazy(() => import('./components/ConfirmApplicationEmail'));
const CandidateGroupInvitation = lazy(() => import('./components/CandidateGroupInvitation'));
const CandidateProfile = lazy(() => import('./components/CandidateProfile'));
const SetupWizard = lazy(() => import('./components/setup/SetupWizard'));
const RecordingDelete = lazy(() => import('./components/interviewVideo/RecordingDelete'));
const InterviewerPreview = lazy(() => import('./components/interviewer/InterviewerPreview'));
const PublicProfile = lazy(() => import('./components/PublicProfile'));
const TalentSearch = lazy(() => import('./components/TalentSearch'));
const Fractional = lazy(() => import('./components/fractional/Fractional'));
const FractionalExperts = lazy(() => import('./components/fractional/FractionalExperts'));
const Screening = lazy(() => import('./components/Screening'));
const Premium = lazy(() => import('./components/Premium'));
const Organizations = lazy(() => import('./components/Organizations'));
const IasEmployers = lazy(() => import('./components/IasEmployers'));
const InterviewInvite = lazy(() => import('./components/InterviewInvite'));
const InterviewScheduling = lazy(() => import('./components/InterviewScheduling'));
const InterviewerAvailability = lazy(() => import('./components/InterviewerAvailability'));
const InterviewMeeting = lazy(() => import('./components/InterviewMeeting'));
const OfferSigning = lazy(() => import('./components/OfferSigning'));
const ApplicationProgress = lazy(() => import('./components/ApplicationProgress'));
const Vendors = lazy(() => import('./components/Vendors'));
const VendorDashboard = lazy(() => import('./components/VendorDashboard'));

/**
 * /careers with no organization named.
 *
 * Deliberately not a directory: listing every organization on the platform
 * would let anyone enumerate the customer base. A careers page is reached by
 * the link its organization publishes.
 */
const CareersLanding = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
      textAlign: 'center',
    }}
  >
    <Box>
      <Typography variant="h5" gutterBottom>
        Careers
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Each organization has its own careers page. Use the link they published —
        it looks like <code>/careers/their-name</code>.
      </Typography>
    </Box>
  </Box>
);

// Layout for internal pages (with sidebar)
/**
 * The internal routes, remounted whenever the viewed organization changes.
 *
 * The platform operator switches organization from the nav, and every page
 * below fetches its own data on mount. Without the key they would keep
 * showing the previous organization's rows until something else happened to
 * trigger a refetch; with it, switching is a clean reload of the view.
 *
 * It lives here rather than in App because it needs the auth context, and App
 * is what provides it.
 */
const ScopedRoutes = ({ children }) => {
  const { actingOrgId } = useAuth();
  return <Routes key={actingOrgId || 'all-organizations'}>{children}</Routes>;
};

// Layout for career details page (without sidebar)
const CareerLayout = ({ children }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CareerDetailsHeader />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // 24px here plus the container's own gutter left 40px of margin
          // either side of a 375px screen.
          p: { xs: 2, sm: 3 },
          width: '100%',
        }}
      >
        {children}
      </Box>
      <Footer />
    </Box>
  );
};

// Layout for apply job page (without sidebar)
const ApplyJobLayout = ({ children }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ApplyJobHeader />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
        }}
      >
        {children}
      </Box>
      <Footer />
    </Box>
  );
};

// Layout for start interview page (without sidebar)
// The page owns the conversation now, so there is nothing for this layout to
// share between the header and the page — and nothing to clear from under a
// fixed header that is no longer fixed.
const StartInterviewLayout = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    <StartInterviewHeader />
    <Box component="main" sx={{ flexGrow: 1, width: '100%' }}>
      {children}
    </Box>
    <Footer />
  </Box>
);

/** `/learning` is what Upskill used to be called. Its tab and type choices are in the address and are kept. */
export const LegacyLearningLink = () => {
  const { search } = useLocation();
  return <Navigate to={`/upskill${search}`} replace />;
};

/** `/jobs/:id` was written into some notifications; the requisition page is `/job/:id`. */
export const LegacyJobLink = () => {
  const { id } = useParams();
  return <Navigate to={`/job/${id}`} replace />;
};

function App() {
  return (
    <ThemeModeProvider>
      <AuthProvider>
        <MarketProvider>
        <MarketSync />
        <Router>
          <VisitTracker />
          <Suspense fallback={<Box aria-busy sx={{ minHeight: '100vh' }} />}>
          <Routes>
          {/* On an employer's own address there is no product pitch and no signup: staff are invited, and the front door is the sign-in. */}
          <Route path="/" element={brand.employer ? <Navigate to="/login" replace /> : <Home />} />
          <Route path="/for-job-seekers" element={brand.employer ? <Navigate to="/login" replace /> : <Home audience="seekers" />} />
          <Route path="/for-hiring-teams" element={brand.employer ? <Navigate to="/login" replace /> : <Home audience="hiring" />} />
          <Route path="/contact" element={brand.employer ? <Navigate to="/login" replace /> : <ContactSales />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={brand.employer ? <Navigate to="/login" replace /> : <Signup />} />
          <Route path="/employers/signup" element={brand.employer ? <Navigate to="/login" replace /> : <EmployerSignup />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          {LANDING_PATHS.map((route) => <Route key={route} path={route} element={brand.employer ? <Navigate to="/login" replace /> : <LandingPage path={route} />} />)}
          <Route path="/job-bot" element={<JobBot />} />
          <Route path="/confirm-email" element={<ConfirmApplicationEmail />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/signup/complete" element={<SignupComplete />} />
          <Route path="/group-invite/:token" element={<CandidateGroupInvitation />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<SetPassword mode="reset" />} />
          <Route path="/activate" element={<SetPassword mode="activate" />} />
          <Route path="/careers" element={<CareersLanding />} />
          {process.env.NODE_ENV !== 'production' && <Route path="/dev/interviewer" element={<InterviewerPreview />} />}
          <Route path="/careers/:slug" element={<Careers />} />
          <Route path="/p/:slug" element={<PublicProfile />} />
          <Route path="/interview/invite/:token" element={<InterviewInvite />} />
          <Route path="/interview/recording/:id" element={<RecordingDelete />} />
          {/* Where a candidate chooses the time of their interview with the hiring
              manager. The address is the credential, so there is no sign-in. */}
          <Route path="/schedule/:token" element={<InterviewScheduling />} />
          {/* The person interviewing, who has no account: when they are free, and one meeting of theirs. */}
          <Route path="/interviewer/:token" element={<InterviewerAvailability />} />
          <Route path="/interviewing/:token" element={<InterviewMeeting />} />
          {/* A candidate reads and signs their offer letter; the company's signer countersigns. */}
          <Route path="/offer/sign/:token" element={<OfferSigning kind="company" />} />
          <Route path="/offer/:token" element={<OfferSigning kind="candidate" />} />
          {/* Where an applicant waits while their resume is read and scored.
              The id is in the address rather than in router state, so a refresh
              finds the same application. */}
          <Route
            path="/application/:applicationId"
            element={
              <ApplyJobLayout>
                <ApplicationProgress />
              </ApplyJobLayout>
            }
          />
          <Route
            path="/careers/:slug/:id"
            element={
              <CareerLayout>
                <CareerDetails />
              </CareerLayout>
            }
          />
          <Route
            path="/apply-job/:id"
            element={
              <ApplyJobLayout>
                <ApplyJob />
              </ApplyJobLayout>
            }
          />
          <Route
            path="/start-interview/:id"
            element={
              <StartInterviewLayout>
                <StartInterview />
              </StartInterviewLayout>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
              <InternalLayout>
                <ScopedRoutes>
                  <Route
                    path="/jobs"
                    element={
                      <RequireCapability capability="page.jobs">
                        <JobList />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/job/:id"
                    element={
                      <RequireCapability capability="page.jobs">
                        <JobDetails />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/create-job"
                    element={
                      <RequireCapability capability="page.create_job">
                        <CreateJob />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/candidate"
                    element={
                      <RequireCapability capability="page.candidates">
                        <Candidates />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/candidate-profile"
                    element={
                      <RequireCapability capability="page.candidate_profile">
                        <CandidateProfile />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/setup"
                    element={
                      <RequireCapability capability="page.candidate_profile">
                        <SetupWizard />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/talent"
                    element={
                      <RequireCapability capability="page.talent">
                        <TalentSearch />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/premium"
                    element={
                      <RequireCapability capability="page.premium">
                        <Premium />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/interviews"
                    element={
                      <RequireCapability capability="page.interviews">
                        <Interviews />
                      </RequireCapability>
                    }
                  />
                  {/* The bell's "an interview has been assessed" links here: the list with that interview opened. */}
                  <Route
                    path="/interviews/:interviewId"
                    element={
                      <RequireCapability capability="page.interviews">
                        <Interviews />
                      </RequireCapability>
                    }
                  />
                  {/* Notifications sent before the requisition page was found at /job/:id still say /jobs/:id. */}
                  <Route path="/jobs/:id" element={<LegacyJobLink />} />
                  <Route
                    path="/resumes"
                    element={
                      <RequireCapability capability="page.resumes">
                        <Resumes />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/match"
                    element={
                      <RequireCapability capability="page.match">
                        <Match />
                      </RequireCapability>
                    }
                  />
                  <Route path="/dashboard" element={<RequireCapability capability="page.dashboard"><Dashboard /></RequireCapability>} />
                  <Route path="/reports" element={<RequireCapability capability="page.reports"><div>Reports Page</div></RequireCapability>} />
                  <Route path="/profile" element={<RequireCapability capability="page.profile"><Profile /></RequireCapability>} />
                  <Route path="/no-access" element={<Box sx={{ p: 3 }}><Typography>Your role currently has no page access. Contact your Super Admin.</Typography></Box>} />
                  <Route
                    path="/candidate-workspace"
                    element={<RequireCapability capability="page.candidate_workspace"><CandidateWorkspace /></RequireCapability>}
                  />
                  {/* Practice interviews. Deliberately not pages of their own
                      in ACCESS_PAGES: they are reached from a row in My
                      applications and have nothing to show without one, so
                      they inherit the workspace's capability rather than
                      adding a nav item and a dashboard tile for something
                      nobody would navigate to directly. */}
                  <Route
                    path="/candidate-workspace/mock-interview/:id"
                    element={<RequireCapability capability="page.candidate_workspace"><MockInterview /></RequireCapability>}
                  />
                  <Route
                    path="/candidate-workspace/assessment/:id"
                    element={<RequireCapability capability="page.candidate_workspace"><MockAssessment /></RequireCapability>}
                  />
                  {/* People lifted out of the Administration screen's first tab.
                      Guarded on the same capability that tab was. */}
                  <Route path="/people" element={<RequireCapability capability="platform.users.view"><PeoplePage /></RequireCapability>} />
                  <Route path="/network" element={<RequireCapability capability="page.network"><Network /></RequireCapability>} />
                  <Route path="/events" element={<RequireCapability capability="page.events"><Events /></RequireCapability>} />
                  <Route path="/feedback" element={<RequireCapability capability="page.feedback"><Feedback /></RequireCapability>} />
                  {/* Publicly sourced jobs. Its own page, not another tab on the
                      candidate workspace: nothing here was posted through this
                      product and none of it can be applied to inside it. */}
                  <Route path="/job-board" element={<RequireCapability capability="page.job_board"><JobBoard /></RequireCapability>} />
                  <Route path="/cost" element={<RequireCapability capability="page.cost"><Cost /></RequireCapability>} />
                  <Route path="/visitors" element={<RequireCapability capability="page.visitors"><Visitors /></RequireCapability>} />
                  {/* Job matches is a tab of Jobs. This address stays for the emails and notifications that already point at it. */}
                  <Route path="/matches" element={<Navigate to="/candidate-workspace?tab=matches" replace />} />
                  <Route path="/upskill" element={<RequireCapability capability="page.learning"><Learning /></RequireCapability>} />
                  <Route path="/fractional" element={<RequireCapability capability="page.fractional"><Fractional /></RequireCapability>} />
                  <Route path="/fractional-experts" element={<RequireCapability capability="page.fractional_hire"><FractionalExperts /></RequireCapability>} />
                  <Route path="/screening" element={<RequireCapability capability="page.screening"><Screening /></RequireCapability>} />
                  {/* It was called Learning: old bookmarks and links keep working, with what they asked for. */}
                  <Route path="/learning" element={<LegacyLearningLink />} />
                  <Route path="/tasks" element={<RequireCapability capability="page.tasks"><TasksWorkspace /></RequireCapability>} />
                  <Route path="/notes" element={<RequireCapability capability="page.notes"><NotesWorkspace /></RequireCapability>} />
                  <Route path="/search" element={<RequireCapability capability="page.tasks"><SearchResults /></RequireCapability>} />
                  <Route path="/categories" element={<RequireCapability capability="page.tasks"><PersonalCategories /></RequireCapability>} />
                  <Route path="/categories/:categoryId" element={<RequireCapability capability="page.tasks"><PersonalCategoryItems /></RequireCapability>} />
                  <Route path="/shared-with-me" element={<RequireCapability capability="page.shared_with_me"><SharedWithMe /></RequireCapability>} />
                  <Route
                    path="/calendar"
                    element={<RequireCapability capability="page.calendar"><Calendar /></RequireCapability>}
                  />
                  <Route
                    path="/organizations"
                    element={
                      <RequireCapability capability="page.organizations">
                        <Organizations />
                      </RequireCapability>
                    }
                  />
                  {/* The platform's view of the employers using Interview as a Service. */}
                  <Route
                    path="/employers"
                    element={
                      <RequireCapability capability="page.organizations">
                        <IasEmployers />
                      </RequireCapability>
                    }
                  />
                  {/* Kept so existing links and bookmarks still land somewhere;
                      it is the same screen. */}
                  <Route
                    path="/submissions"
                    element={
                      <RequireCapability capability="page.candidates">
                        <Candidates />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/vendors"
                    element={
                      <RequireCapability capability="page.vendors">
                        <Vendors />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/vendor-dashboard"
                    element={
                      <RequireCapability capability="page.assigned_roles">
                        <VendorDashboard />
                      </RequireCapability>
                    }
                  />
                  <Route
                    path="/administration"
                    element={
                      <RequireCapability capability="page.administration">
                        <Administration />
                      </RequireCapability>
                    }
                  />
                </ScopedRoutes>
              </InternalLayout>
              </ProtectedRoute>
            }
          />
          </Routes>
          </Suspense>
        </Router>
        </MarketProvider>
      </AuthProvider>
    </ThemeModeProvider>
  );
}

export default App;
